// 레이어 타입 정의
export interface Layer {
  id: string;           // 레이어 고유 ID
  name: string;         // 레이어 이름
  type: LayerType;      // 레이어 타입
  visible: boolean;     // 표시 여부
  opacity: number;      // 투명도 (0-1)
  order?: number;       // 레이어 순서 (선택적)
  source?: string;      // 데이터 소스 (EGIS, 사용자 등)
  url?: string;         // WMS/WMTS URL (EGIS 레이어용)
  params?: any;         // WMS 파라미터 (EGIS 레이어용)
  fileCount?: number;   // 파일 개수 (로컬 레이어용)
  files?: any[];        // 파일 목록 (로컬 레이어용)
  metadata?: LayerMetadata; // 메타데이터
  legendStyle?: any;    // 범례 스타일 (범례 편집기에서 설정)

  selectedFileIndex?: number; // 선택된 파일 인덱스 (사용자 정의 레이어용)
  groupId?: string;     // 그룹 ID (그룹 레이어 구조용)
  rotationSettings?: any; // 회전 설정 (레이어 설정에서 설정)
}

// 레이어 타입 열거형
export enum LayerType {
  EGIS = 'egis',           // EGIS 토지피복 레이어
  USER_TIF = 'user_tif',   // 사용자 TIF 파일
  USER_GEOJSON = 'user_geojson', // 사용자 GeoJSON 파일
  USER_SHAPEFILE = 'user_shapefile', // 사용자 Shapefile
  USER_KML = 'user_kml',   // 사용자 KML 파일
  USER_CSV = 'user_csv',   // 사용자 CSV 파일
  USER_LAYER = 'user_layer', // 사용자 정의 레이어 (파일 업로드 가능)
  BACKGROUND = 'background', // 배경지도 레이어
  GROUP = 'group'          // 그룹 레이어
}

// 레이어 메타데이터 인터페이스
export interface LayerMetadata {
  description?: string;    // 레이어 설명
  createdDate?: Date;      // 생성일
  modifiedDate?: Date;     // 수정일
  fileSize?: number;       // 파일 크기 (바이트)
  coordinateSystem?: string; // 좌표계
  bounds?: [number, number, number, number]; // 경계 좌표
  classification?: ClassificationInfo; // 분류 정보
  uploadedFiles?: UploadedFile[]; // 업로드된 파일 목록
  legendStyle?: any; // 범례 스타일 (LegendStyle 타입)
}

// 분류 정보 인터페이스
export interface ClassificationInfo {
  system: string;          // 분류 체계 (예: 환경부 토지피복)
  categories: Category[];  // 분류 카테고리
}

// 분류 카테고리 인터페이스
export interface Category {
  code: string;            // 분류 코드
  name: string;            // 분류명
  color: string;           // 표시 색상
  description?: string;    // 설명
}

// 레이어 그룹 인터페이스 (확장)
export interface LayerGroup {
  id: string;              // 그룹 ID
  name: string;            // 그룹 이름
  type: 'background' | 'egis' | 'user'; // 그룹 타입
  layers: Layer[];         // 그룹 내 레이어들
  expanded: boolean;       // 확장 여부
  order: number;           // 그룹 순서 (0: 배경지도, 1: EGIS, 2: 사용자)
  visible: boolean;        // 그룹 전체 표시 여부
  opacity: number;         // 그룹 전체 투명도
}

// 그룹 레이어 관리 인터페이스
export interface LayerGroupManager {
  groups: LayerGroup[];    // 모든 그룹
  addGroup: (group: LayerGroup) => void;
  removeGroup: (groupId: string) => void;
  updateGroup: (groupId: string, updates: Partial<LayerGroup>) => void;
  addLayerToGroup: (groupId: string, layer: Layer) => void;
  removeLayerFromGroup: (groupId: string, layerId: string) => void;
  moveLayerBetweenGroups: (layerId: string, fromGroupId: string, toGroupId: string) => void;
  toggleGroupExpanded: (groupId: string) => void;
  toggleGroupVisible: (groupId: string) => void;
  setGroupOpacity: (groupId: string, opacity: number) => void;
}

// 파일 업로드 정보 인터페이스
export interface FileUploadInfo {
  file: File;              // 업로드 파일
  type: LayerType;         // 레이어 타입
  name: string;            // 레이어 이름
  description?: string;    // 설명
}

// 회전 정보 인터페이스
export interface RotationInfo {
  has_exif: boolean;       // EXIF 정보 존재 여부
  orientation?: number;    // EXIF 방향 값 (1-8)
  needs_rotation: boolean; // 회전 필요 여부
  rotation_angle: number;  // 회전 각도 (0, 90, 180, 270)
  image_size?: [number, number]; // 이미지 크기 [width, height]
  detection_method?: string; // 감지 방법 ('exif' | 'image_analysis')
}

// 업로드된 파일 정보 인터페이스
export interface UploadedFile {
  id: string;              // 파일 ID
  name: string;            // 파일명
  size: number;            // 파일 크기
  uploadDate: Date;        // 업로드 날짜
  type: string;            // 파일 타입
  status: 'active' | 'inactive'; // 활성 상태
  rotation_info?: RotationInfo; // 회전 정보
}

// 레이어 설정 인터페이스
export interface LayerSettings {
  opacity: number;         // 투명도
  visible: boolean;        // 표시 여부
  style?: LayerStyle;      // 스타일 설정
}

// 레이어 스타일 인터페이스
export interface LayerStyle {
  fillColor?: string;      // 채우기 색상
  strokeColor?: string;    // 선 색상
  strokeWidth?: number;    // 선 두께
  fillOpacity?: number;    // 채우기 투명도
} 